import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {isLoaded as isLoadedPackages, PackageModel, UnassignedPackageModel} from '../PackageModel';
import {Loaded} from 'Core/models/Async';

type PinCareProps = {
  packageName: StringUnion;
  planType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  replacement: boolean;
  policyNumber: PolicyNumber;
  packageList: PackageModel;
  units: StringUnion;
  jStamp: boolean;
  situsState: StringUnion;
};

export class InvalidGroupPinCare {
  public readonly key = 'InvalidGroupPinCare';

  constructor(
    public readonly packageName: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly packageList: PackageModel,
    public readonly units: StringUnion,
    public readonly jStamp: boolean,
    public readonly situsState: StringUnion
  ) {}
}

export class ValidGroupPinCare {
  public static createApp({
    packageName,
    planType,
    premium,
    calculatedPremium,
    cwa,
    replacement,
    policyNumber,
    packageList,
    units,
    jStamp,
    situsState,
  }: PinCareProps) {
    return this.create(
      packageName,
      planType,
      premium,
      calculatedPremium,
      cwa,
      replacement,
      policyNumber,
      packageList,
      units,
      jStamp,
      situsState
    );
  }

  public static create(
    packageName: StringUnion,
    planType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    replacement: boolean,
    policyNumber: PolicyNumber,
    packageList: PackageModel,
    units: StringUnion,
    jStamp: boolean,
    situsState: StringUnion
  ) {
    return isNonEmptyString(packageName) &&
      planType &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      replacement &&
      policyNumber &&
      isLoadedPackages(packageList)
      ? new ValidGroupPinCare(
          packageName,
          planType,
          premium,
          calculatedPremium,
          cwa,
          replacement,
          policyNumber,
          packageList,
          units,
          jStamp,
          situsState
        )
      : new InvalidGroupPinCare(
          packageName,
          planType,
          premium,
          calculatedPremium,
          cwa,
          replacement,
          policyNumber,
          packageList,
          units,
          jStamp,
          situsState
        );
  }

  public readonly key = 'ValidGroupPinCare';
  constructor(
    public readonly packageName: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly packageList: Loaded<object[]>,
    public readonly units: StringUnion,
    public readonly jStamp: boolean,
    public readonly situsState: StringUnion
  ) {}
}

export type GroupPinCare = InvalidGroupPinCare | ValidGroupPinCare;

export type GroupPinCareOptions<TReturn> = ToPartialMatchOptions<GroupPinCare, 'key', TReturn>;

export const matchGroupPinCare = <TReturn>(options: GroupPinCareOptions<TReturn>) => (app: GroupPinCare): TReturn => {
  switch (app.key) {
    case 'InvalidGroupPinCare':
      return options.InvalidGroupPinCare ? options.InvalidGroupPinCare(app) : options.default(app);
    case 'ValidGroupPinCare':
      return options.ValidGroupPinCare ? options.ValidGroupPinCare(app) : options.default(app);
  }
};

export const EmptyGroupPinCare = new InvalidGroupPinCare(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  EmptyPolicyNumber,
  new UnassignedPackageModel(),
  new EmptyString(),
  false,
  new EmptyString()
);

export const changePackage = (packageName: string) => (app: GroupPinCare) =>
  ValidGroupPinCare.createApp({...app, packageName: NonEmptyString.create(packageName)});

export const changePlanType = (planType: string) => (app: GroupPinCare) =>
  ValidGroupPinCare.createApp({...app, planType: NonEmptyString.create(planType)});

export const changePremium = (premium: string) => (app: GroupPinCare) =>
  ValidGroupPinCare.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCalculatedPremium = (calculatedPremium: string) => (app: GroupPinCare) =>
  ValidGroupPinCare.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: GroupPinCare) =>
  ValidGroupPinCare.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeReplacement = (replacement: boolean) => (app: GroupPinCare) =>
  ValidGroupPinCare.createApp({...app, replacement, policyNumber: ValidPolicyNumber.create(new EmptyString())});

export const changePolicyNumber = (policyNumber: StringUnion) => (app: GroupPinCare) =>
  ValidGroupPinCare.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changePackageList = (packages: PackageModel) => (app: GroupPinCare) =>
  ValidGroupPinCare.createApp({...app, packageList: packages});

export const changeUnits = (units: string) => (app: GroupPinCare) =>
  ValidGroupPinCare.createApp({...app, units: NonEmptyString.create(units)});

export const changeJStamp = (jStamp: boolean) => (app: GroupPinCare) => ValidGroupPinCare.createApp({...app, jStamp});

export const changeSitusState = (situsState: string) => (app: GroupPinCare) =>
  ValidGroupPinCare.createApp({...app, situsState: NonEmptyString.create(situsState)});

export const changePackageItem = (packageItem: object) => (app: GroupPinCare) =>
  ValidGroupPinCare.createApp({...app, units: NonEmptyString.create(units[packageItem['packageName']])});

export type riderCodeType = {code: string; displayName: string; propertyName: string; units: number[]};

const riderCodes = [
  {
    code: '',
    displayName: '',
    propertyName: '',
    units: [1, 2, 3, 4, 5, 6, 7],
  },
];

export const units = {
  A: 'PINNACLECARE',
  B: 'PINNACLECARE PREMIER',
  C: 'PINNACLECARE PREMIER+',
  D: 'PINNACLECARE EMPLOYER SELECT ERISA',
  E: 'PINNACLECARE PREMIER EMPLOYER SELECT ERISA',
  F: 'PINNACLECARE PREMIER+ EMPLOYER SELECT ERISA',
  G: 'PINNACLECARE EXECUTIVE',
};

export const getRiderUnits = (riderCode: string | undefined) => {
  if (riderCode !== undefined && riderCode !== 'None') {
    const index = riderCodes.findIndex(rc => rc.code === riderCode);
    return riderCodes[index].units;
  }
  return [];
};

export const getRiderByPropertyName = (propertyName: string | undefined) => {
  if (propertyName !== undefined) {
    const index = riderCodes.findIndex(rc => rc.propertyName === propertyName);
    return riderCodes[index];
  }
  return {};
};

export const getAllRiderCodes = () => riderCodes;
